"use client";

import { useMemo } from "react";
import type { IBrandQuoteThemeConfig } from "@/@sdk/brands/brand-model";
import type { iBrand } from "@/@sdk/brands/brand-model";
import HolidayQuoteView from "@/components/quotes/quotes-template/holiday-quote-view";
import { cn } from "@/lib/utils";
import {
  buildQuoteThemePreviewFixture,
  buildQuoteThemePreviewOrganization,
} from "./quote-theme-sample-fixture";

export function QuoteThemePreview({
  brand,
  quoteThemeConfig,
  variant = "sidebar",
}: {
  brand: iBrand | null | undefined;
  quoteThemeConfig: IBrandQuoteThemeConfig;
  variant?: "sidebar" | "fullscreen";
}) {
  const fixture = useMemo(
    () => buildQuoteThemePreviewFixture(brand?.name ?? "Your Brand", brand),
    [brand]
  );

  const previewOrganization = useMemo(
    () => buildQuoteThemePreviewOrganization(brand) ?? fixture.organization,
    [brand, fixture.organization]
  );

  const previewBrand = useMemo(
    (): iBrand | undefined =>
      brand
        ? {
            ...brand,
            quoteThemeConfig,
          }
        : undefined,
    [brand, quoteThemeConfig]
  );

  const isFullscreen = variant === "fullscreen";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-xl border bg-muted/30",
        isFullscreen && "h-full rounded-none border-0"
      )}
    >
      <div className="shrink-0 border-b bg-background px-4 py-2">
        <p className="text-sm font-medium">Live preview</p>
        <p className="text-xs text-muted-foreground">
          Sample quote with your current theme selections
        </p>
      </div>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto bg-background",
          isFullscreen ? "" : "max-h-[720px]"
        )}
      >
        <div
          className={cn(
            "origin-top",
            isFullscreen ? "" : "scale-[0.72] sm:scale-[0.82]"
          )}
        >
          <HolidayQuoteView
            quote={fixture.quote}
            customer={fixture.customer}
            organization={previewOrganization}
            brand={previewBrand}
            agent={fixture.agent}
            quotesDomain="https://preview.holidayquotes.app"
            footerConfig={brand?.footerConfig}
          />
        </div>
      </div>
    </div>
  );
}
